// Parallaxe-Möblierung des Bahnkorridors (Konzept §12.1 Punkt 1+2).
//
// Das ist die billigste und wirksamste Investition des Projekts: Nahbereich
// rast, Mittelgrund zieht, Horizont steht — und das entsteht fast komplett
// aus dem, was *direkt am Gleis* steht. Ein Repeating-Kit trägt mehr
// Bewegungsgefühl als jeder See.
//
// Architektur: EIN InstancedMesh pro Objekttyp für den ganzen Sichtbereich.
// Nicht pro Segment instanzieren — sonst multiplizieren sich die Draw Calls
// mit der Segmentzahl und sprengen das Budget (~60 für den ganzen Korridor).

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { WORLD_SEED } from '../../shared/constants.js';
import {
  TRACK_LENGTH, TRACK_FEATURES, STATIONS, trackFrame, featureAt,
  BRANCH_MAIN, BRANCH_MINE, trackLength, projectToTrack, JUNCTION_MAIN_S,
} from '../../shared/track.js';
import { terrainHeight } from '../../shared/terrain.js';
import { rand01, chance } from '../../shared/rng.js';
import { mat, box, cylinder, STYLE_RULES } from '../render/style.js';
import { COL_SOLID } from './collision.js';

// --- Konstanten -------------------------------------------------------------

const VIEW_RADIUS = 320;          // m entlang s — etwas weiter alss Gleis (Parallaxe)
const REBUILD_STEP = 40;          // s-Delta, ab dem Instanzen neu befüllt werden
const POLE_SPACING = 48;          // m — Regelmäßigkeit *ist* das Geschwindigkeitsgefühl
const FENCE_SPACING = 3.2;
const SIGNAL_SPACING = 380;
const KM_SPACING = 1000;
const MIN = STYLE_RULES.minDetailSize;

// Seed-Kanäle, damit Objektarten nicht korrelieren.
const SEED = {
  fence: WORLD_SEED ^ 0xf3a1,
  scrap: WORLD_SEED ^ 0x5c2a,
  pile: WORLD_SEED ^ 0xb011,
  duct: WORLD_SEED ^ 0xd0c7,
  stack: WORLD_SEED ^ 0x517a,
  wheel: WORLD_SEED ^ 0xeee1,
  concrete: WORLD_SEED ^ 0xc0a1,
  hut: WORLD_SEED ^ 0x8117,
  cross: WORLD_SEED ^ 0xc205,
  side: WORLD_SEED ^ 0x51de,
  culvert: WORLD_SEED ^ 0xc01a,
  drift: WORLD_SEED ^ 0xd21f,
};

// Kapazitäten: obere Schranke für Instanzen im Sichtfenster (±VIEW_RADIUS).
// Lange Hanganschnitte (~1,5 km Feature, Sichtfenster 640 m) brauchen Extra.
// Jeder Typ = 1 Draw Call — Budget ~60 für den ganzen Korridor.
const CAP = {
  pole: 40,
  crossarm: 40,
  fence: 480,
  km: 8,
  signal: 12,
  pile: 50,
  duct: 90,
  stack: 24,
  scrap: 80,
  wheel: 12,
  concrete: 120,
  hut: 8,
  car: 4,
  bridgeBeam: 200,
  bridgePier: 24,
  cutWall: 360,
  embank: 220,
  wash: 80,
  culvert: 32,
  abutment: 16,
  wing: 64,
  drain: 160,
  lamp: 24,
};

// Modul-Puffer — nichts pro Frame allokieren.
const _frame = {
  pos: { x: 0, y: 0, z: 0 },
  tangent: { x: 0, y: 0, z: 0 },
  right: { x: 0, y: 0, z: 0 },
  heading: 0,
};
const _pos = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _euler = new THREE.Euler(0, 0, 0, 'YXZ');
const _mat4 = new THREE.Matrix4();
const _scale = new THREE.Vector3(1, 1, 1);
const _color = new THREE.Color();
const _focus = { s: 0 };
const _startS = STATIONS[0].s;

export class CorridorSystem {
  async init(ctx) {
    this.group = new THREE.Group();
    this.group.name = 'corridor';
    ctx.scene.add(this.group);

    this.meshes = {};
    this.counts = {};
    this._lastRebuildS = -1e9;
    this._lastBranch = BRANCH_MAIN;
    this._nightLights = []; // punktuelle Lampen (Signale, Relaishäuschen)
    /** Asset-Metadaten: welcher Kit Fuß-Ursprung hat (y=Boden statt Mitte). */
    this._footOrigin = new Set();
    /** Vollmast (catenary) ersetzt pole+crossarm. */
    this._fullMast = false;
    /** Zaunsegment statt nur Pfahl. */
    this._fenceSegment = false;

    this.assets = ctx.assets ?? null;
    if (this.assets?.loadMany) {
      await this.assets.loadMany([
        { name: 'catenary_mast' },
        { name: 'signal_mast' },
        { name: 'signal' },
        { name: 'mile_marker' },
        { name: 'fence_post' },
        { name: 'fence_segment' },
        { name: 'sleeper_stack' },
        { name: 'scrap_pile_1' },
        { name: 'scrap_pile_2' },
        { name: 'scrap_pile_3' },
        { name: 'bridge_segment' },
        { name: 'bridge_pier' },
        { name: 'crossing' },
        { name: 'waiting_car' },
        { name: 'relay_hut' },
      ]);
    }

    this._buildKits(ctx);
    this._rebuild(this._focusS(ctx));
    // Brückenpfeiler sind ortsfest — einmal ins Raster, nicht pro Rebuild.
    this._registerColliders(ctx);
  }

  /**
   * Feste Korridor-Hindernisse (Pfeiler + Widerlager). Zäune/Masten bewusst
   * nicht: hunderte pro km, Parallaxe, und §15 will kaum Physik.
   * Stützmauern/Flügel: melden falls Klettern daran nötig wird — collision.js
   * ist hier tabu (anderer Subagent).
   */
  _registerColliders(ctx) {
    const col = ctx.collision ?? ctx.systems?.collision;
    if (!col) return;
    for (const f of TRACK_FEATURES) {
      if (f.type !== 'bridge') continue;
      const branch = f.branch ?? BRANCH_MAIN;
      const pierH = f.height ?? 26;
      const pierStep = 28;
      const p0 = Math.ceil(f.s0 / pierStep) * pierStep;
      const halfH = pierH * 0.5;
      for (let s = p0; s <= f.s1; s += pierStep) {
        trackFrame(s, _frame, branch);
        const top = _frame.pos.y - 1.2;
        const cy = top - halfH;
        col.addBox(_frame.pos.x, cy, _frame.pos.z, 1.2, halfH, 1.2, _frame.heading, COL_SOLID);
        // Seitlicher Ansatz
        const lat = 9.0;
        const px = _frame.pos.x + _frame.right.x * lat;
        const pz = _frame.pos.z + _frame.right.z * lat;
        col.addBox(px, cy, pz, 0.9, halfH * 0.85, 0.9, _frame.heading, COL_SOLID);
      }
      // Widerlager an beiden Enden — massiv, kletterbar relevant.
      for (const sEnd of [f.s0, f.s1]) {
        trackFrame(sEnd, _frame, branch);
        const ah = Math.min(12, pierH * 0.45);
        col.addBox(
          _frame.pos.x, _frame.pos.y - 1.0 - ah * 0.5, _frame.pos.z,
          4.5, ah * 0.5, 3.2, _frame.heading, COL_SOLID,
        );
      }
    }
  }

  update(_dt, ctx) {
    const s = this._focusS(ctx);
    const br = this._focusBranch(ctx);
    if (Math.abs(s - this._lastRebuildS) >= REBUILD_STEP || br !== this._lastBranch) {
      this._rebuild(s, br);
    }
    this._updateNightLights(ctx);
  }

  /** Anzahl aktiver Draw-Calls dieses Systems (eine pro InstancedMesh + Lampen). */
  drawCallEstimate() {
    let n = 0;
    for (const m of Object.values(this.meshes)) {
      if (m && m.count > 0) n++;
    }
    // Lampen: je eine PointLight, die nachts an ist.
    for (const L of this._nightLights) {
      if (L.light.visible) n++;
    }
    return n;
  }

  /**
   * Für Tests / Shots: erzwingt Rebuild um s.
   * Zweites Argument: Branch (main|mine), sonst letzter / main.
   */
  forceRebuild(s, branch) {
    this._rebuild(s, branch);
  }

  /**
   * Für Tests: liefert alle gesetzten Instanzen eines Typs (Positionen).
   */
  getInstancePositions(type) {
    const mesh = this.meshes[type];
    if (!mesh) return [];
    const out = [];
    const m = new THREE.Matrix4();
    const p = new THREE.Vector3();
    for (let i = 0; i < mesh.count; i++) {
      mesh.getMatrixAt(i, m);
      p.setFromMatrixPosition(m);
      out.push({ x: p.x, y: p.y, z: p.z });
    }
    return out;
  }

  // --- Kit-Geometrien -------------------------------------------------------

  /**
   * Extrahiert eine einzige BufferGeometry aus einem geladenen glTF
   * (Weltkoordinaten des Modell-Roots). Paint-Meshes werden übersprungen —
   * die bleiben Station/Paint vorbehalten. Ergebnis eignet sich für
   * InstancedMesh: ein Draw Call, Blender-Silhouette.
   */
  _geoFromAsset(name, { skipPaint = true } = {}) {
    const record = this.assets?.models?.get(name);
    if (!record?.scene) return null;
    const scene = record.scene;
    scene.updateMatrixWorld(true);
    const geos = [];
    scene.traverse((obj) => {
      if (!obj.isMesh || !obj.geometry) return;
      if (skipPaint && obj.name?.startsWith('paint_')) return;
      // Nur position+normal: glTF-Meshes haben oft unterschiedliche UVs —
      // mergeGeometries verlangt identische Attribute.
      const src = obj.geometry;
      if (!src.getAttribute('normal')) src.computeVertexNormals();
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', src.getAttribute('position').clone());
      g.setAttribute('normal', src.getAttribute('normal').clone());
      if (src.index) g.setIndex(src.index.clone());
      g.applyMatrix4(obj.matrixWorld);
      geos.push(g);
    });
    if (geos.length === 0) return null;
    if (geos.length === 1) return geos[0];
    const merged = mergeGeometries(geos, false);
    for (const g of geos) g.dispose();
    if (!merged) return null;
    return merged;
  }

  _buildKits(ctx) {
    const mk = (name, geo, matOrKey, cap, opts = {}) => {
      const material = typeof matOrKey === 'string' ? mat(matOrKey) : matOrKey;
      const mesh = new THREE.InstancedMesh(geo, material, cap);
      mesh.name = `corridor:${name}`;
      mesh.frustumCulled = true;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      mesh.castShadow = opts.castShadow ?? false;
      mesh.receiveShadow = opts.receiveShadow ?? true;
      mesh.count = 0;
      this.group.add(mesh);
      this.meshes[name] = mesh;
      this.counts[name] = 0;
      if (opts.footOrigin) this._footOrigin.add(name);
      return mesh;
    };

    // Masten: glTF-Vollmast (inkl. Traverse) oder Code-Zylinder + Traverse.
    const mastGeo = this._geoFromAsset('catenary_mast');
    if (mastGeo) {
      this._fullMast = true;
      mk('pole', mastGeo, 'concreteDark', CAP.pole, { castShadow: true, footOrigin: true });
      // crossarm-Mesh bleibt für API/Tests, wird aber nicht befüllt.
      mk('crossarm', box(3.2, 0.12, 0.14), 'steelDark', CAP.crossarm);
    } else {
      mk('pole', cylinder(0.14, 0.18, 7.0, 6), 'concreteDark', CAP.pole, { castShadow: true });
      mk('crossarm', box(3.2, 0.12, 0.14), 'steelDark', CAP.crossarm);
    }

    // Zaun: Segment (3.2 m) bevorzugt, sonst Pfahl.
    const fenceGeo = this._geoFromAsset('fence_segment')
      || this._geoFromAsset('fence_post')
      || box(0.12, 1.15, 0.12);
    this._fenceSegment = !!this.assets?.models?.has('fence_segment')
      || !!this.assets?.models?.has('fence_post');
    mk('fence', fenceGeo, 'woodDark', CAP.fence, {
      footOrigin: this._fenceSegment,
    });

    // Kilometerstein.
    const kmGeo = this._geoFromAsset('mile_marker') || box(0.35, 0.85, 0.22);
    mk('km', kmGeo, 'concrete', CAP.km, {
      footOrigin: !!this.assets?.models?.has('mile_marker'),
    });

    // Signal: volles Lichtsignal, sonst Mast, sonst Box.
    const signalGeo = this._geoFromAsset('signal')
      || this._geoFromAsset('signal_mast')
      || box(0.28, 4.6, 0.28);
    mk('signal', signalGeo, 'steelDark', CAP.signal, {
      castShadow: true,
      footOrigin: !!(this.assets?.models?.has('signal') || this.assets?.models?.has('signal_mast')),
    });

    // Schotterhaufen bleibt Code (kein 1:1-Asset).
    mk('pile', box(1.6, 0.55, 1.1), 'ballastDark', CAP.pile);

    // Kabelkanal am Bankett.
    mk('duct', box(0.55, 0.22, 1.8), 'concreteDark', CAP.duct);

    // Gestapelte Schwellen.
    const stackGeo = this._geoFromAsset('sleeper_stack') || box(2.4, 0.7, 1.0);
    mk('stack', stackGeo, 'sleeper', CAP.stack, {
      castShadow: true,
      footOrigin: !!this.assets?.models?.has('sleeper_stack'),
    });

    // Schrott: Variante 1 als Basis-Geometrie (Varianten 2/3 per Scale/Yaw).
    const scrapGeo = this._geoFromAsset('scrap_pile_1')
      || this._geoFromAsset('scrap_pile_2')
      || box(0.9, 0.35, 0.55);
    mk('scrap', scrapGeo, 'railRust', CAP.scrap, {
      footOrigin: !!(this.assets?.models?.has('scrap_pile_1') || this.assets?.models?.has('scrap_pile_2')),
    });

    // Alter Radsatz.
    mk('wheel', cylinder(0.45, 0.45, 1.4, 8), 'steelDark', CAP.wheel);

    // Betonteile.
    mk('concrete', box(1.2, 0.5, 0.8), 'concrete', CAP.concrete);

    // Relaishäuschen.
    const hutGeo = this._geoFromAsset('relay_hut') || box(2.8, 2.2, 2.2);
    mk('hut', hutGeo, 'corrugated', CAP.hut, {
      castShadow: true,
      footOrigin: !!this.assets?.models?.has('relay_hut'),
    });

    // Wartendes Auto.
    const carGeo = this._geoFromAsset('waiting_car') || box(1.7, 1.35, 4.0);
    mk('car', carGeo, 'steel', CAP.car, {
      castShadow: true,
      footOrigin: !!this.assets?.models?.has('waiting_car'),
    });

    // Brücke: Segment (10 m) oder Code-Träger.
    const bridgeSeg = this._geoFromAsset('bridge_segment');
    if (bridgeSeg) {
      this._bridgeSegment = true;
      mk('bridgeBeam', bridgeSeg, 'steelDark', CAP.bridgeBeam, { castShadow: true });
    } else {
      this._bridgeSegment = false;
      mk('bridgeBeam', box(1.0, 0.9, 4.0), 'steelDark', CAP.bridgeBeam, { castShadow: true });
    }

    const pierGeo = this._geoFromAsset('bridge_pier') || box(2.4, 8.0, 2.4);
    mk('bridgePier', pierGeo, 'concreteDark', CAP.bridgePier, {
      castShadow: true,
      footOrigin: !!this.assets?.models?.has('bridge_pier'),
    });

    // Bahnübergang als eigener Typ (zusätzlich zu car/stack).
    const crossGeo = this._geoFromAsset('crossing');
    if (crossGeo) {
      this._hasCrossing = true;
      mk('crossing', crossGeo, 'steelDark', 6, { castShadow: true });
    } else {
      this._hasCrossing = false;
    }

    // Einschnitt: Stützmauer-Platten. rock (nicht rockDark) — sonst schlucken
    // Lambert+Schatten die Form zu reinem Schwarz und die Strecke verschwindet.
    mk('cutWall', box(0.4, 2.8, 3.2), 'rock', CAP.cutWall, { castShadow: true });

    // Damm: Böschungskronen / Fußkanten.
    mk('embank', box(0.35, 0.7, 3.2), 'concrete', CAP.embank);

    // Wash: Betonsohle quer zur Strecke (Arroyo-Wanne) — breit unter dem Bett.
    mk('wash', box(10.0, 0.3, 3.5), 'concrete', CAP.wash);

    // Durchlass: Betonrohr-Maul in der Böschung.
    mk('culvert', cylinder(0.55, 0.55, 3.2, 8), 'concreteDark', CAP.culvert, { castShadow: true });

    // Widerlager: massiver Betonblock an Brückenenden.
    mk('abutment', box(5.5, 4.0, 3.5), 'concreteDark', CAP.abutment, { castShadow: true });

    // Flügelmauer: auslaufende Mauer am Feature-Übergang.
    mk('wing', box(0.35, 1.8, 2.6), 'concrete', CAP.wing, { castShadow: true });

    // Entwässerungsgraben: flache Rinne am Einschnittfuß.
    mk('drain', box(0.55, 0.28, 3.2), 'concreteDark', CAP.drain);

    // Signallampen-Gehäuse (emissiv nachts umgeschaltet).
    this.lampMat = mat('steel', { emissive: 0x000000, emissiveIntensity: 0 });
    mk('lamp', box(0.25, 0.25, 0.25), this.lampMat, CAP.lamp);

    // PointLights für Nacht — wenige, hart, Konzept §12.4 / §17.
    this._lampPool = [];
    for (let i = 0; i < 12; i++) {
      const light = new THREE.PointLight(0xffaa66, 0, 28, 2.0);
      light.visible = false;
      this.group.add(light);
      this._lampPool.push(light);
    }
    void ctx;
  }

  // --- Rebuild --------------------------------------------------------------

  /**
   * @param {number} sCenter Bogenlänge Fokus
   * @param {string} [branch] Strecke (default: aus playerState.branch oder main)
   */
  _rebuild(sCenter, branch) {
    const br = branch
      ?? this._lastBranch
      ?? BRANCH_MAIN;
    this._lastRebuildS = sCenter;
    this._lastBranch = br;
    const len = trackLength(br);
    const s0 = Math.max(0, sCenter - VIEW_RADIUS);
    const s1 = Math.min(len, sCenter + VIEW_RADIUS);

    // Zähler resetten.
    for (const k of Object.keys(this.counts)) this.counts[k] = 0;
    this._nightLights.length = 0;

    // Möblierung nur auf der Fokus-Strecke (Parallaxe am aktuellen Gleis).
    this._placePoles(s0, s1, br);
    this._placeFences(s0, s1, br);
    this._placeKm(s0, s1, br);
    this._placeSignals(s0, s1, br);
    this._placeIndustrial(s0, s1, br);
    this._placeFeatures(s0, s1, br);
    this._placeCrossings(s0, s1, br);

    // Nahe der Weiche: Nebenstrecken-Features mit einblenden (sichtbarer Abzweig).
    if (br === BRANCH_MAIN && sCenter > JUNCTION_MAIN_S - VIEW_RADIUS
      && sCenter < JUNCTION_MAIN_S + VIEW_RADIUS) {
      const mineS = Math.max(0, sCenter - JUNCTION_MAIN_S);
      const m0 = Math.max(0, mineS - VIEW_RADIUS * 0.5);
      const m1 = Math.min(trackLength(BRANCH_MINE), mineS + VIEW_RADIUS);
      this._placeFeatures(m0, m1, BRANCH_MINE);
    }

    // count auf die Meshes schreiben.
    for (const [name, mesh] of Object.entries(this.meshes)) {
      mesh.count = this.counts[name] ?? 0;
      if (mesh.count > 0) mesh.instanceMatrix.needsUpdate = true;
      // Bounding sphere grob — sonst culling bei count-Änderung falsch.
      mesh.computeBoundingSphere?.();
    }
  }

  _push(type, x, y, z, yaw, sx = 1, sy = 1, sz = 1) {
    const mesh = this.meshes[type];
    if (!mesh) return -1;
    const i = this.counts[type] ?? 0;
    // Kapazität = array.length/16 der instanceMatrix (beim Bau gesetzt).
    const maxN = mesh.instanceMatrix.array.length / 16;
    if (i >= maxN) return -1;
    _pos.set(x, y, z);
    _euler.set(0, yaw, 0);
    _quat.setFromEuler(_euler);
    _scale.set(sx, sy, sz);
    _mat4.compose(_pos, _quat, _scale);
    mesh.setMatrixAt(i, _mat4);
    this.counts[type] = i + 1;
    return i;
  }

  _placePoles(s0, s1, branch = BRANCH_MAIN) {
    // Strikt regelmäßig — der gleichmäßige Rhythmus *ist* das Tempo.
    const first = Math.ceil(s0 / POLE_SPACING) * POLE_SPACING;
    const foot = this._footOrigin.has('pole');
    for (let s = first; s <= s1; s += POLE_SPACING) {
      // Seite alternierend, aber deterministisch stabil.
      const side = (Math.floor(s / POLE_SPACING) % 2 === 0) ? 1 : -1;
      const lat = side * 3.1;
      trackFrame(s, _frame, branch);
      // Nicht auf Brücken in den Abgrund stellen.
      const feat = featureAt(s, branch);
      if (feat?.type === 'bridge') continue;
      const x = _frame.pos.x + _frame.right.x * lat;
      const z = _frame.pos.z + _frame.right.z * lat;
      const ground = terrainHeight(x, z, s);
      // Modell: Fuß auf Boden; Code-Zylinder: Mitte bei +3.5.
      // catenary_mast: Traverse zeigt nach -X → bei +lateral um 180° drehen.
      const yaw = this._fullMast
        ? _frame.heading + (side > 0 ? Math.PI : 0)
        : _frame.heading;
      this._push('pole', x, foot ? ground : ground + 3.5, z, yaw);
      if (!this._fullMast) {
        this._push(
          'crossarm',
          _frame.pos.x + _frame.right.x * (lat * 0.35),
          ground + 6.6,
          _frame.pos.z + _frame.right.z * (lat * 0.35),
          _frame.heading
        );
      }
    }
  }

  _placeFences(s0, s1, branch = BRANCH_MAIN) {
    const first = Math.ceil(s0 / FENCE_SPACING) * FENCE_SPACING;
    const foot = this._footOrigin.has('fence');
    for (let s = first; s <= s1; s += FENCE_SPACING) {
      const si = Math.floor(s);
      // Zaun nicht durchgängig — Lücken für Bahnübergänge und Höfe.
      if (isInYardRange(s, branch)) continue;
      if (featureAt(s, branch)?.type === 'bridge') continue;
      // Welche Seite(n): oft beide, manchmal eine.
      const both = chance(0.72, si, SEED.fence);
      const sides = both ? [-1, 1] : [chance(0.5, si, SEED.side) ? 1 : -1];
      for (const side of sides) {
        // Gelegentliche Lücke im Zaun.
        if (chance(0.08, si, side + 3, SEED.fence)) continue;
        const lat = side * 5.4;
        trackFrame(s, _frame, branch);
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        const ground = terrainHeight(x, z, s);
        this._push('fence', x, foot ? ground : ground + 0.55, z, _frame.heading);
      }
    }
  }

  _placeKm(s0, s1, branch = BRANCH_MAIN) {
    const first = Math.ceil(s0 / KM_SPACING) * KM_SPACING;
    const foot = this._footOrigin.has('km');
    for (let s = first; s <= s1; s += KM_SPACING) {
      if (s <= 0) continue;
      trackFrame(s, _frame, branch);
      const lat = -4.2;
      const x = _frame.pos.x + _frame.right.x * lat;
      const z = _frame.pos.z + _frame.right.z * lat;
      const ground = terrainHeight(x, z, s);
      this._push('km', x, foot ? ground : ground + 0.42, z, _frame.heading);
    }
  }

  _placeSignals(s0, s1, branch = BRANCH_MAIN) {
    const first = Math.ceil(s0 / SIGNAL_SPACING) * SIGNAL_SPACING;
    const foot = this._footOrigin.has('signal');
    for (let s = first; s <= s1; s += SIGNAL_SPACING) {
      if (s < 80) continue;
      const si = Math.floor(s);
      // Signale vor Höfen und an Feature-Grenzen dichter wirken lassen:
      // Basisraster reicht; gelegentlich auslassen.
      if (chance(0.12, si, SEED.side)) continue;
      const side = chance(0.5, si, SEED.side) ? 1 : -1;
      const lat = side * 3.6;
      trackFrame(s, _frame, branch);
      if (featureAt(s, branch)?.type === 'bridge') continue;
      const x = _frame.pos.x + _frame.right.x * lat;
      const z = _frame.pos.z + _frame.right.z * lat;
      const ground = terrainHeight(x, z, s);
      // signal.glb: Kopf zeigt nach -Z.
      this._push('signal', x, foot ? ground : ground + 2.3, z, _frame.heading);
      // Lampe am Signal.
      const li = this._push('lamp', x, ground + 4.2, z, _frame.heading);
      this._attachNightLight(x, ground + 4.2, z, 0.55);
      void li;
    }
  }

  _placeIndustrial(s0, s1, branch = BRANCH_MAIN) {
    // Industrielle Möblierung mit eigenem Rhythmus — nicht jedes Meter, aber
    // oft genug, dass das Bild nach Bahn aussieht statt nach Naturspaziergang.
    const step = 14;
    const first = Math.ceil(s0 / step) * step;
    for (let s = first; s <= s1; s += step) {
      const si = Math.floor(s);
      if (isInYardRange(s, branch)) continue; // Höfe möbliert StationSystem
      const feat = featureAt(s, branch);
      if (feat?.type === 'bridge') continue;

      trackFrame(s, _frame, branch);

      // Schotterhaufen
      if (chance(0.18, si, SEED.pile)) {
        const side = chance(0.5, si, SEED.side) ? 1 : -1;
        const lat = side * (6.5 + rand01(si, SEED.pile) * 3);
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        const y = terrainHeight(x, z, s) + 0.28;
        const sc = 0.7 + rand01(si, 3, SEED.pile) * 0.6;
        this._push('pile', x, y, z, _frame.heading + rand01(si, 9, SEED.pile), sc, sc, sc);
      }

      // Kabelkanal — häufiger, näher am Gleis, regelmäßiger.
      if (si % 28 < 18 && chance(0.55, si, SEED.duct)) {
        const side = (si % 56 < 28) ? 1 : -1;
        const lat = side * 4.0;
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        const y = terrainHeight(x, z, s) + 0.12;
        this._push('duct', x, y, z, _frame.heading);
      }

      // Gestapelte Schwellen
      if (chance(0.07, si, SEED.stack)) {
        const side = chance(0.5, si, SEED.side + 1) ? 1 : -1;
        const lat = side * (7.0 + rand01(si, SEED.stack) * 2);
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        const ground = terrainHeight(x, z, s);
        const y = this._footOrigin.has('stack') ? ground : ground + 0.35;
        this._push('stack', x, y, z, _frame.heading + 0.3);
      }

      // Schrott im Graben
      if (chance(0.12, si, SEED.scrap)) {
        const side = chance(0.5, si, SEED.scrap) ? 1 : -1;
        const lat = side * (8 + rand01(si, SEED.scrap) * 5);
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        const ground = terrainHeight(x, z, s);
        const y = this._footOrigin.has('scrap') ? ground : ground + 0.18;
        // Leichte Scale-Variation simuliert scrap_pile_2/3.
        const sc = 0.85 + rand01(si, 4, SEED.scrap) * 0.4;
        this._push('scrap', x, y, z, rand01(si, 2, SEED.scrap) * Math.PI, sc, sc, sc);
      }

      // Alter Radsatz
      if (chance(0.025, si, SEED.wheel)) {
        const side = chance(0.5, si, SEED.wheel) ? 1 : -1;
        const lat = side * 7.5;
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        const y = terrainHeight(x, z, s) + 0.45;
        // Zylinder liegt auf der Seite: yaw + roll über scale-trick — yaw reicht.
        this._push('wheel', x, y, z, _frame.heading + Math.PI / 2);
      }

      // Betonteile
      if (chance(0.06, si, SEED.concrete)) {
        const side = chance(0.5, si, SEED.concrete) ? 1 : -1;
        const lat = side * (6.8 + rand01(si, SEED.concrete) * 2);
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        const y = terrainHeight(x, z, s) + 0.25;
        this._push('concrete', x, y, z, _frame.heading);
      }

      // Relaishäuschen — selten, markant.
      if (chance(0.015, si, SEED.hut) && si % 5 === 0) {
        const side = chance(0.5, si, SEED.hut) ? 1 : -1;
        const lat = side * 9.5;
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        const ground = terrainHeight(x, z, s);
        const y = this._footOrigin.has('hut') ? ground : ground + 1.1;
        this._push('hut', x, y, z, _frame.heading);
        this._attachNightLight(x, ground + 2.5, z, 0.7);
        this._push('lamp', x, ground + 2.6, z, _frame.heading);
      }
    }
  }

  /**
   * Bauwerke entlang der Features. branch filtert — Mine-Features dürfen
   * nicht auf der Hauptachse landen (früherer Bug: embankment@mine s=850
   * erschien auf main@850 als Erdhaufen ohne Grund).
   */
  _placeFeatures(s0, s1, branch = BRANCH_MAIN) {
    for (const f of TRACK_FEATURES) {
      const fb = f.branch ?? BRANCH_MAIN;
      if (fb !== branch) continue;
      if (f.type === 'yard') continue; // nur Markierung, Planum aus terrain.js
      if (f.s1 < s0 || f.s0 > s1) continue;
      const a = Math.max(f.s0, s0);
      const b = Math.min(f.s1, s1);
      if (f.type === 'bridge') this._placeBridge(a, b, f, branch);
      else if (f.type === 'cutting') this._placeCutting(a, b, f, branch);
      else if (f.type === 'embankment') this._placeEmbankment(a, b, f, branch);
      else if (f.type === 'wash') this._placeWash(a, b, f, branch);
    }
  }

  /**
   * Stahlbrücke: Träger (Asset oder Code), Pfeiler, Widerlager, Flügelmauern.
   * Ohne Widerlager endet die Brücke „im Nichts" — der Nutzer sieht dann nur
   * ein Gleis über dem Abgrund statt gebauter Infrastruktur.
   */
  _placeBridge(s0, s1, f, branch = BRANCH_MAIN) {
    if (this._bridgeSegment) {
      // 10-m-Stahlbrücken-Kacheln: Ursprung = Gleismitte auf Schienenoberkante.
      const beamStep = 10.0;
      const first = Math.ceil(s0 / beamStep) * beamStep;
      for (let s = first; s <= s1; s += beamStep) {
        trackFrame(s, _frame, branch);
        this._push('bridgeBeam', _frame.pos.x, _frame.pos.y, _frame.pos.z, _frame.heading);
      }
    } else {
      // Stahlbrücke: Längsträger links/rechts, Geländer-Silhouette oben.
      const beamStep = 4.0;
      const first = Math.ceil(s0 / beamStep) * beamStep;
      for (let s = first; s <= s1; s += beamStep) {
        trackFrame(s, _frame, branch);
        const y = _frame.pos.y - 0.55;
        for (const lat of [-2.0, 2.0]) {
          const x = _frame.pos.x + _frame.right.x * lat;
          const z = _frame.pos.z + _frame.right.z * lat;
          this._push('bridgeBeam', x, y, z, _frame.heading, 1, 1, 1);
        }
        for (const lat of [-2.6, 2.6]) {
          const x = _frame.pos.x + _frame.right.x * lat;
          const z = _frame.pos.z + _frame.right.z * lat;
          this._push('bridgeBeam', x, _frame.pos.y + 1.4, z, _frame.heading, 0.25, 1.6, 0.9);
        }
      }
    }

    // Pfeiler alle ~28 m — Höhe aus Feature.
    const pierH = f.height ?? 26;
    const pierStep = 28;
    const p0 = Math.ceil(s0 / pierStep) * pierStep;
    const foot = this._footOrigin.has('bridgePier');
    const baseH = 8.0;
    for (let s = p0; s <= s1; s += pierStep) {
      // Nicht direkt in die Widerlager-Zone stellen.
      if (s < f.s0 + 12 || s > f.s1 - 12) continue;
      trackFrame(s, _frame, branch);
      const scaleY = Math.max(0.5, pierH / baseH);
      const top = _frame.pos.y - 1.2;
      const y = foot ? top - baseH * scaleY : top - (baseH * scaleY) * 0.5;
      this._push('bridgePier', _frame.pos.x, y, _frame.pos.z, _frame.heading, 1, scaleY, 1);
      // Seitlicher Ansatz (Malfläche bridge:pier-south).
      const lat = 9.0;
      const px = _frame.pos.x + _frame.right.x * lat;
      const pz = _frame.pos.z + _frame.right.z * lat;
      this._push('bridgePier', px, y, pz, _frame.heading, 0.7, scaleY * 0.85, 0.7);
    }

    // Widerlager an Feature-Enden (auch wenn Sichtfenster nur die Mitte sieht,
    // sobald s0/s1 das Ende einschließt).
    for (const sEnd of [f.s0, f.s1]) {
      if (sEnd < s0 - 1 || sEnd > s1 + 1) continue;
      this._placeAbutment(sEnd, f, branch, sEnd === f.s0 ? 1 : -1);
    }
  }

  /** Massives Widerlager + Flügelmauern, die in den Hang auslaufen. */
  _placeAbutment(s, f, branch, dirSign) {
    trackFrame(s, _frame, branch);
    const pierH = f.height ?? 26;
    const ah = Math.min(10, Math.max(4, pierH * 0.4));
    // Block unter Gleisniveau, leicht versetzt in Streckenrichtung (dirSign).
    const along = dirSign * 2.2;
    const tx = _frame.tangent?.x ?? Math.sin(_frame.heading);
    const tz = _frame.tangent?.z ?? Math.cos(_frame.heading);
    // trackFrame liefert tangent — nutzen.
    trackFrame(s, _frame, branch);
    const ax = _frame.pos.x + _frame.tangent.x * along * 0;
    const az = _frame.pos.z + _frame.tangent.z * along * 0;
    const y = _frame.pos.y - ah * 0.35;
    this._push('abutment', ax, y, az, _frame.heading, 1, ah / 4.0, 1.15);

    // Flügelmauern schräg abfallend in den Hang (Übergang Bauwerk → Gelände).
    for (const side of [-1, 1]) {
      for (let k = 0; k < 3; k++) {
        const lat = side * (4.5 + k * 2.2);
        const alongW = dirSign * (1.5 + k * 2.5);
        const x = _frame.pos.x + _frame.right.x * lat + _frame.tangent.x * alongW;
        const z = _frame.pos.z + _frame.right.z * lat + _frame.tangent.z * alongW;
        const ground = terrainHeight(x, z, s);
        const hScale = Math.max(0.45, (ah * (1 - k * 0.28)) / 2.2);
        const wy = ground + 1.1 * hScale;
        // Leicht aus der Streckenachse gedreht → „Flügel".
        const yaw = _frame.heading + side * (0.35 + k * 0.12);
        this._push('wing', x, wy, z, yaw, 1, hScale, 1.1 - k * 0.15);
      }
    }
    void tx; void tz;
  }

  /**
   * Einschnitt: Stützmauer, Kronenkante, Entwässerungsgraben, Flügel an Enden.
   * Terrain formt die Böschung — ohne Mauer sieht es aus wie ein Graben.
   * Y immer relativ zur Gleishöhe (trackY), nicht zu groundW-Mitte: sonst
   * schweben Platten, wenn Terrain-Kacheln noch fehlen oder die Böschung steil ist.
   */
  _placeCutting(s0, s1, f, branch = BRANCH_MAIN) {
    const depth = f.depth ?? 6;
    // Bewusst lückig: volle Wandplatte alle 3 m würde den Korridor zumaurn
    // und die Schiene verstecken. Echte Einschnitte haben Stützmauer-Segmente
    // mit sichtbarer Böschung dazwischen.
    const step = 9.0;
    const first = Math.ceil(s0 / step) * step;
    // Box 2,8 m hoch — Scale mild, max ~4 m sichtbare Wand.
    const hScale = Math.max(0.7, Math.min(1.5, (depth * 0.55) / 2.8));
    const wallH = 2.8 * hScale;

    for (let s = first; s <= s1; s += step) {
      trackFrame(s, _frame, branch);
      const trackY = _frame.pos.y;
      for (const side of [-1, 1]) {
        const latW = side * 5.4;
        const xw = _frame.pos.x + _frame.right.x * latW;
        const zw = _frame.pos.z + _frame.right.z * latW;
        const yWall = trackY + wallH * 0.35;
        this._push('cutWall', xw, yWall, zw, _frame.heading, 1, hScale, 1);

        // Kronenkante nur an jedem 2. Segment (weniger clutter).
        if (Math.floor(s / step) % 2 === 0) {
          const latC = side * (7.5 + Math.min(2.5, depth * 0.25));
          const xc = _frame.pos.x + _frame.right.x * latC;
          const zc = _frame.pos.z + _frame.right.z * latC;
          const groundC = terrainHeight(xc, zc, s);
          const yCrown = Math.max(groundC, trackY + depth * 0.4) + 0.2;
          this._push('embank', xc, yCrown, zc, _frame.heading, 0.85, 0.45, 1);
        }

        // Entwässerungsgraben am Fuß.
        const latD = side * 3.7;
        const xd = _frame.pos.x + _frame.right.x * latD;
        const zd = _frame.pos.z + _frame.right.z * latD;
        this._push('drain', xd, trackY - 0.45, zd, _frame.heading);
      }
    }

    this._placeFeatureWings(f, s0, s1, branch, depth * 0.4, 5.4);
  }

  /**
   * Damm: Kronenkante, Böschungsfuß, Durchlässe, bei großer Höhe Stützmauer,
   * Flügel an den Enden. Genau das unterscheidet Bauwerk von Erdhaufen.
   * Y-Anker: trackY − height (Fuß) bzw. trackY (Krone) — nicht free-float.
   */
  _placeEmbankment(s0, s1, f, branch = BRANCH_MAIN) {
    const height = f.height ?? 6;
    const step = 4.0;
    const first = Math.ceil(s0 / step) * step;
    const tall = height >= 4.5;

    for (let s = first; s <= s1; s += step) {
      trackFrame(s, _frame, branch);
      const trackY = _frame.pos.y;
      for (const side of [-1, 1]) {
        // Krone: Betonkante nahe Schotterbett.
        const latTop = side * 3.8;
        const xt = _frame.pos.x + _frame.right.x * latTop;
        const zt = _frame.pos.z + _frame.right.z * latTop;
        this._push('embank', xt, trackY - 0.7, zt, _frame.heading);

        // Böschungsfuß: Unterkante der Schüttung (trackY − height).
        const latToe = side * (6.0 + height * 0.5);
        const xToe = _frame.pos.x + _frame.right.x * latToe;
        const zToe = _frame.pos.z + _frame.right.z * latToe;
        const gToe = terrainHeight(xToe, zToe, s);
        const yToe = Math.min(gToe, trackY - height * 0.85) + 0.35;
        this._push('embank', xToe, yToe, zToe, _frame.heading, 1.1, 0.65, 1.15);

        // Bei höheren Dämmen: gelegentliche Stützmauer-Segmente (nicht durchgehend —
        // sonst sieht der Damm aus wie eine Tunnelwand).
        if (tall && Math.floor(s / step) % 3 === 0) {
          const latR = side * (5.0 + height * 0.15);
          const xr = _frame.pos.x + _frame.right.x * latR;
          const zr = _frame.pos.z + _frame.right.z * latR;
          const hs = Math.max(0.6, Math.min(1.3, height / 4.0));
          const yR = trackY - height * 0.55;
          this._push('cutWall', xr, yR, zr, _frame.heading, 0.75, hs, 0.9);
        }
      }
    }

    // Durchlässe: alle ~48 m ein Rohr-Maul in der Böschung.
    const culvStep = 48;
    const c0 = Math.ceil(s0 / culvStep) * culvStep;
    for (let s = c0; s <= s1; s += culvStep) {
      if (chance(0.12, Math.floor(s), SEED.culvert)) continue;
      trackFrame(s, _frame, branch);
      const trackY = _frame.pos.y;
      for (const side of [-1, 1]) {
        const lat = side * (5.2 + height * 0.35);
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        // Maul auf ca. 70 % Dammfuß-Höhe — sichtbar in der Böschung.
        const y = trackY - height * 0.7;
        this._push(
          'culvert', x, y, z,
          _frame.heading + Math.PI / 2,
          1.15, 0.9, 1.15,
        );
      }
      // Mittelstück unter dem Gleis.
      this._push(
        'culvert', _frame.pos.x, trackY - height * 0.55, _frame.pos.z,
        _frame.heading + Math.PI / 2,
        1.35, 0.95, 1.5,
      );
    }

    this._placeFeatureWings(f, s0, s1, branch, height * 0.45, 4.2);
  }

  /**
   * Trockenfluss (Arroyo): Betonsohle, Flügelmauern, Hochwassermarken,
   * angeschwemmtes Treibholz. Kein echter Fluss — Season-1-Südwest-Atmosphäre.
   */
  _placeWash(s0, s1, f, branch = BRANCH_MAIN) {
    const step = 3.5;
    const first = Math.ceil(s0 / step) * step;
    for (let s = first; s <= s1; s += step) {
      trackFrame(s, _frame, branch);
      const trackY = _frame.pos.y;
      const yFloor = trackY - 2.0;
      // Betonsohle unter der Querung (breit, flach) — Scale breiter als Gleis.
      this._push('wash', _frame.pos.x, yFloor, _frame.pos.z, _frame.heading, 1.25, 1, 1);

      for (const side of [-1, 1]) {
        // Seitliche Wangen der Wanne.
        const lat = side * 5.5;
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        this._push('wing', x, yFloor + 0.95, z, _frame.heading, 1, 0.95, 1.1);
        // Äußere Leitwand / Hochwasser.
        const lat2 = side * 8.0;
        const x2 = _frame.pos.x + _frame.right.x * lat2;
        const z2 = _frame.pos.z + _frame.right.z * lat2;
        this._push('concrete', x2, yFloor + 1.15, z2, _frame.heading, 0.65, 1.35, 1.05);
      }
    }

    // Hochwassermarken an den Wangen.
    for (let s = Math.ceil(s0 / 18) * 18; s <= s1; s += 18) {
      trackFrame(s, _frame, branch);
      for (const side of [-1, 1]) {
        const lat = side * 5.6;
        const x = _frame.pos.x + _frame.right.x * lat;
        const z = _frame.pos.z + _frame.right.z * lat;
        this._push('concrete', x, _frame.pos.y - 1.0, z, _frame.heading, 0.35, 0.22, 1.3);
      }
    }

    // Treibholz im Bett.
    for (let s = Math.ceil(s0 / 22) * 22; s <= s1; s += 22) {
      const si = Math.floor(s);
      if (!chance(0.55, si, SEED.drift)) continue;
      trackFrame(s, _frame, branch);
      const side = chance(0.5, si, SEED.side) ? 1 : -1;
      const lat = side * (3.5 + rand01(si, SEED.drift) * 4);
      const x = _frame.pos.x + _frame.right.x * lat;
      const z = _frame.pos.z + _frame.right.z * lat;
      const y = _frame.pos.y - 1.85;
      const sc = 0.7 + rand01(si, 3, SEED.drift) * 0.5;
      this._push('scrap', x, y, z, rand01(si, 2, SEED.drift) * Math.PI, sc, sc * 0.7, sc);
    }

    this._placeFeatureWings(f, s0, s1, branch, 2.0, 6.0);
    void f;
  }

  /**
   * Flügelmauern an Feature-Grenzen — der Übergang ist das, was der Nutzer
   * als „merkwürdig" empfand, wenn er abrupt fehlt.
   * Y an trackY verankert und mit Gelände gemischt, damit nichts schwebt.
   */
  _placeFeatureWings(f, s0, s1, branch, wallH, baseLat) {
    for (const sEnd of [f.s0, f.s1]) {
      if (sEnd < s0 - 2 || sEnd > s1 + 2) continue;
      const dir = sEnd === f.s0 ? -1 : 1; // aus dem Feature hinaus
      trackFrame(sEnd, _frame, branch);
      const trackY = _frame.pos.y;
      for (const side of [-1, 1]) {
        for (let k = 0; k < 3; k++) {
          const lat = side * (baseLat + k * 1.8);
          const along = dir * (2.5 + k * 3.2);
          const x = _frame.pos.x + _frame.right.x * lat + _frame.tangent.x * along;
          const z = _frame.pos.z + _frame.right.z * lat + _frame.tangent.z * along;
          const ground = terrainHeight(x, z, sEnd);
          const fade = 1 - k * 0.28;
          const hs = Math.max(0.4, (wallH * fade) / 2.2);
          const wingH = 2.2 * hs;
          // Mitte zwischen Gleishöhe und Gelände — sitzt auf dem Übergang.
          const yBase = Math.min(trackY - 0.2, ground + 0.2);
          const y = yBase + wingH * 0.45;
          const yaw = _frame.heading + side * (0.28 + k * 0.12) + dir * 0.04;
          this._push('wing', x, y, z, yaw, 1, hs, 1.0 - k * 0.08);
        }
      }
    }
  }

  _placeCrossings(s0, s1, branch = BRANCH_MAIN) {
    // Bahnübergänge nur auf der Hauptstrecke an festen Stellen.
    if (branch !== BRANCH_MAIN) return;
    const candidates = [1450, 4100, 6400];
    for (const s of candidates) {
      if (s < s0 || s > s1) continue;
      if (isInYardRange(s, branch)) continue;
      trackFrame(s, _frame, branch);
      if (this._hasCrossing && this.meshes.crossing) {
        // crossing.glb: Ursprung Gleismitte auf Schienenoberkante.
        this._push('crossing', _frame.pos.x, _frame.pos.y, _frame.pos.z, _frame.heading);
      } else {
        for (const side of [-1, 1]) {
          const lat = side * 6.5;
          const x = _frame.pos.x + _frame.right.x * lat;
          const z = _frame.pos.z + _frame.right.z * lat;
          const ground = terrainHeight(x, z, s);
          const y = this._footOrigin.has('stack') ? ground : ground + 0.5;
          this._push('stack', x, y, z, _frame.heading);
        }
      }
      // Auto wartet auf einer Seite — es muss nicht fahren (§11).
      const side = chance(0.5, Math.floor(s), SEED.cross) ? 1 : -1;
      const lat = side * 11.0;
      const x = _frame.pos.x + _frame.right.x * lat;
      const z = _frame.pos.z + _frame.right.z * lat;
      const ground = terrainHeight(x, z, s);
      const y = this._footOrigin.has('car') ? ground : ground + 0.7;
      this._push('car', x, y, z, _frame.heading + Math.PI / 2);
    }
  }

  _attachNightLight(x, y, z, intensity = 0.6) {
    const light = this._lampPool[this._nightLights.length];
    if (!light) return;
    light.position.set(x, y, z);
    light.intensity = 0; // update schaltet nachts an
    light.userData.baseIntensity = intensity;
    light.visible = false;
    this._nightLights.push({ light, x, y, z, intensity });
  }

  _updateNightLights(ctx) {
    // Konzept §12.4: starkes Licht, harte Kontraste. §17: Nacht ist Prototyp.
    // Keine flächige Beleuchtung — einzelne Lampen an Signalen/Relais.
    const dayness = ctx.sun?.colors?.dayness ?? ctx.sun?.elevation ?? 1;
    const night = dayness < 0.25;
    const t = night ? clamp01(1 - dayness / 0.25) : 0;
    for (const L of this._nightLights) {
      L.light.visible = t > 0.05;
      L.light.intensity = L.intensity * t * 1.8;
    }
    // Emissiv der Lampen-Meshes.
    if (this.lampMat) {
      if (t > 0.05) {
        this.lampMat.emissive.setHex(0xffaa55);
        this.lampMat.emissiveIntensity = 0.9 * t;
      } else {
        this.lampMat.emissive.setHex(0x000000);
        this.lampMat.emissiveIntensity = 0;
      }
    }
  }

  _focusS(ctx) {
    if (ctx.playerState?.trackS != null) return ctx.playerState.trackS;
    if (ctx.trackHint != null && ctx.trackHint >= 0) return ctx.trackHint;
    return _startS;
  }

  /** Strecke des Fokus: explizit am State, sonst über Weltposition, sonst main. */
  _focusBranch(ctx) {
    if (ctx.playerState?.branch === BRANCH_MINE || ctx.playerState?.branch === BRANCH_MAIN) {
      return ctx.playerState.branch;
    }
    if (ctx.trackBranch === BRANCH_MINE || ctx.trackBranch === BRANCH_MAIN) {
      return ctx.trackBranch;
    }
    // Weltposition → nächste Strecke (Weiche/Mine).
    const pos = ctx.playerPos ?? ctx.playerState?.pos;
    if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.z)) {
      const hint = this._focusS(ctx);
      const proj = projectToTrack(pos.x, pos.z, hint);
      if (proj?.branch) return proj.branch;
    }
    return BRANCH_MAIN;
  }

  dispose() {
    for (const mesh of Object.values(this.meshes)) {
      mesh.geometry?.dispose?.();
      // Materialien sind gecacht in style.js — nicht disposen.
    }
    for (const L of this._lampPool ?? []) {
      L.dispose?.();
    }
    this.group?.parent?.remove(this.group);
  }
}

// --- Helfer -----------------------------------------------------------------

function isInYardRange(s, branch = BRANCH_MAIN) {
  if (branch === BRANCH_MINE) {
    // DEAD_END-Planum: grob hintere Hälfte der Mine — keine Korridor-Möblierung.
    const len = trackLength(BRANCH_MINE);
    return s >= len - 450;
  }
  for (const st of STATIONS) {
    if (s >= st.yardStart && s <= st.yardEnd) return true;
  }
  return false;
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export const CORRIDOR_VIEW_RADIUS = VIEW_RADIUS;
export const CORRIDOR_POLE_SPACING = POLE_SPACING;
